import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Search, Paperclip, Send, Menu } from 'lucide-react';
import { authAPI, dmAPI, getErrorMessage, ticketAPI } from '../../utils/api';
import { toast } from 'sonner';

const MessagesNew = () => {
  const [messageSearch, setMessageSearch] = useState('');
  const [selectedThread, setSelectedThread] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [mode, setMode] = useState('support');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [directMessages, setDirectMessages] = useState([]);
  const [isDirectLoading, setIsDirectLoading] = useState(false);
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const loadMessages = async () => {
      try {
        setLoading(true);
        const [userResponse, ticketsResponse] = await Promise.all([
          authAPI.getMe(),
          ticketAPI.getAll(),
        ]);
        const ticketData = ticketsResponse.data || [];
        setCurrentUser(userResponse.data || null);
        setTickets(ticketData);
        if (ticketData.length > 0) {
          setSelectedThread(ticketData[0].id);
        }
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load messages'));
      } finally {
        setLoading(false);
      }
    };

    loadMessages();
  }, []);

  useEffect(() => {
    const loadConversations = async () => {
      try {
        setIsDirectLoading(true);
        const response = await dmAPI.getConversations();
        const data = response.data || [];
        setConversations(data);
        if (data.length > 0 && !selectedConversation) {
          setSelectedConversation(data[0].id);
        }
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load conversations'));
      } finally {
        setIsDirectLoading(false);
      }
    };

    if (mode === 'direct') {
      loadConversations();
    }
  }, [mode]);

  useEffect(() => {
    const loadDirectMessages = async () => {
      if (!selectedConversation) {
        setDirectMessages([]);
        return;
      }

      try {
        setIsDirectLoading(true);
        const response = await dmAPI.getMessages(selectedConversation);
        setDirectMessages(response.data || []);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load messages'));
      } finally {
        setIsDirectLoading(false);
      }
    };

    if (mode === 'direct') {
      loadDirectMessages();
    }
  }, [mode, selectedConversation]);

  useEffect(() => {
    if (mode !== 'direct' || !selectedConversation) {
      return undefined;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      return undefined;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = baseUrl.replace(/^http/, 'ws');
    const socket = new WebSocket(
      `${wsBase}/ws/dm/${selectedConversation}?token=${token}`
    );

    wsRef.current = socket;

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type !== 'dm_message') {
          return;
        }
        const message = payload.message;
        if (!message) {
          return;
        }
        setDirectMessages((prev) => {
          if (prev.some((item) => item.id === message.id)) {
            return prev;
          }
          return [...prev, message];
        });
        setConversations((prev) =>
          prev
            .map((conversation) =>
              conversation.id === payload.conversation_id
                ? {
                    ...conversation,
                    last_message: message.message,
                    updated_at: message.created_at,
                  }
                : conversation
            )
            .sort((a, b) =>
              (b.updated_at || '').localeCompare(a.updated_at || '')
            )
        );
      } catch (error) {
        console.error('Failed to parse websocket message', error);
      }
    };

    socket.onerror = () => {
      toast.error('WebSocket connection error');
    };

    return () => {
      socket.close();
      wsRef.current = null;
    };
  }, [mode, selectedConversation]);

  const formatTime = (value) => {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString();
  };

  const threads = useMemo(() => {
    const source = mode === 'support' ? tickets : conversations;
    return source
      .map((item) => {
        if (mode === 'support') {
          const lastMessage = item.messages?.[item.messages.length - 1];
          return {
            id: item.id,
            name: item.subject || 'Support Ticket',
            avatar: (item.subject || 'Support').slice(0, 2).toUpperCase(),
            lastMessage: lastMessage?.message || 'No messages yet',
            time: formatTime(item.updated_at || item.created_at),
            unread: 0,
            online: true,
          };
        }

        const otherUser = item.other_user;
        return {
          id: item.id,
          name: otherUser?.full_name || otherUser?.email || 'Direct Message',
          avatar: (otherUser?.full_name || otherUser?.email || 'DM')
            .slice(0, 2)
            .toUpperCase(),
          lastMessage: item.last_message || 'No messages yet',
          time: formatTime(item.updated_at || item.created_at),
          unread: 0,
          online: true,
        };
      })
      .filter((thread) => {
        if (!messageSearch.trim()) {
          return true;
        }
        const term = messageSearch.toLowerCase();
        return (
          thread.name.toLowerCase().includes(term) ||
          thread.lastMessage.toLowerCase().includes(term)
        );
      });
  }, [tickets, conversations, messageSearch, mode]);

  const selectedTicket = tickets.find((ticket) => ticket.id === selectedThread);
  const selectedConversationData = conversations.find(
    (conversation) => conversation.id === selectedConversation
  );
  const messages =
    mode === 'support'
      ? (selectedTicket?.messages || []).map((message, index) => ({
          id: `${selectedTicket?.id || 'ticket'}-${index}`,
          content: message.message,
          time: formatTime(message.timestamp),
          isOwn: message.sender_id === currentUser?.id,
        }))
      : (directMessages || []).map((message) => ({
          id: message.id,
          content: message.message,
          time: formatTime(message.created_at),
          isOwn: message.sender_id === currentUser?.id,
        }));

  useEffect(() => {
    if (!messagesEndRef.current) {
      return;
    }
    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, selectedThread, selectedConversation, mode]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const trimmed = newMessage.trim();
    if (!trimmed) {
      return;
    }

    try {
      setIsSending(true);
      if (mode === 'support') {
        if (selectedTicket?.id) {
          await ticketAPI.addMessage(selectedTicket.id, trimmed);
        } else {
          await ticketAPI.create({ subject: 'Support Chat', message: trimmed });
        }
        const ticketsResponse = await ticketAPI.getAll();
        const ticketData = ticketsResponse.data || [];
        setTickets(ticketData);
        if (!selectedTicket && ticketData.length > 0) {
          setSelectedThread(ticketData[0].id);
        }
      } else {
        if (!selectedConversation) {
          toast.error('Select or start a conversation first');
          return;
        }
        const response = await dmAPI.sendMessage(selectedConversation, trimmed);
        const optimistic = response.data?.id
          ? response.data
          : {
              id: `local-${Date.now()}`,
              message: trimmed,
              sender_id: currentUser?.id,
              created_at: new Date().toISOString(),
            };
        setDirectMessages((prev) => [...prev, optimistic]);
        setConversations((prev) =>
          prev
            .map((conversation) =>
              conversation.id === selectedConversation
                ? {
                    ...conversation,
                    last_message: trimmed,
                    updated_at: optimistic.created_at,
                  }
                : conversation
            )
            .sort((a, b) =>
              (b.updated_at || '').localeCompare(a.updated_at || '')
            )
        );
      }
      setNewMessage('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send message'));
    } finally {
      setIsSending(false);
    }
  };

  const handleUserSearch = async () => {
    const query = userSearch.trim();
    if (query.length < 2) {
      setUserResults([]);
      return;
    }

    try {
      setIsSearchingUsers(true);
      const response = await dmAPI.searchUsers(query);
      setUserResults(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to search users'));
    } finally {
      setIsSearchingUsers(false);
    }
  };

  const handleStartConversation = async (userId) => {
    try {
      setIsDirectLoading(true);
      const response = await dmAPI.createConversation(userId);
      const conversation = response.data;
      const conversationsResponse = await dmAPI.getConversations();
      setConversations(conversationsResponse.data || []);
      setSelectedConversation(conversation.id);
      setIsNewChatOpen(false);
      setUserSearch('');
      setUserResults([]);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to start conversation'));
    } finally {
      setIsDirectLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #062136;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
        `}</style>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <div
      className="space-y-6 p-4 md:p-6 lg:pl-10 lg:pr-6 lg:py-10 animate-fadeIn bg-[#F9F9F9] flex h-full min-h-0 flex-col"
      data-testid="messages-page"
    >
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          Messages
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm md:text-base">
          Chat with support and community members
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            variant={mode === 'support' ? 'default' : 'outline'}
            onClick={() => setMode('support')}
            className={
              mode === 'support'
                ? 'bg-[#062136] text-white border-[#062136]'
                : 'bg-white text-gray-800 border-gray-400 hover:bg-gray-100 hover:text-gray-900'
            }
          >
            Support
          </Button>
          <Button
            variant={mode === 'direct' ? 'default' : 'outline'}
            onClick={() => setMode('direct')}
            className={
              mode === 'direct'
                ? 'bg-[#062136] text-white border-[#062136]'
                : 'bg-white text-gray-800 border-gray-400 hover:bg-gray-100 hover:text-gray-900'
            }
          >
            Direct Messages
          </Button>
        </div>
      </div>

      <div className="grid flex-1 min-h-0 grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Message Threads List */}
        <Card className="lg:col-span-1 flex min-h-0 flex-col lg:h-[calc(100dvh-240px)]">
          <div className="mb-4 p-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search messages..."
                  value={messageSearch}
                  onChange={(e) => setMessageSearch(e.target.value)}
                  className="flex-1 p-5 dark:bg-slate-900 dark:border-slate-700"
                  style={{ backgroundColor: '#f8f9fa', color: 'var(--input-text-color, #000)' }}
                />
              </div>
              {mode === 'direct' && (
                <Button variant="outline" onClick={() => setIsNewChatOpen(true)}>
                  New Chat
                </Button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {threads.map((thread) => (
              <div
                key={thread.id}
                onClick={() =>
                  mode === 'support'
                    ? setSelectedThread(thread.id)
                    : setSelectedConversation(thread.id)
                }
                className={`flex items-center space-x-3 p-3 border border-gray-100 dark:border-gray-800 cursor-pointer transition-colors ${(mode === 'support'
                  ? selectedThread === thread.id
                  : selectedConversation === thread.id)
                  ? 'bg-blue-50 dark:bg-blue-900/20'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
              >
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center font-bold">
                    {thread.avatar}
                  </div>
                  {thread.online && (
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                      {thread.name}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                    {thread.lastMessage}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {thread.time}
                  </p>
                </div>
                {thread.unread > 0 && (
                  <span className="ml-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {thread.unread}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Chat Conversation */}
        <Card
          className="lg:col-span-2 flex flex-col min-h-0 lg:h-[calc(100dvh-240px)]"
        >
          {/* Chat Header */}
          <div className="flex items-center justify-between space-x-3 p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center font-bold">
                  {mode === 'support'
                    ? selectedTicket?.subject
                      ? selectedTicket.subject.slice(0, 2).toUpperCase()
                      : 'ST'
                    : selectedConversationData?.other_user?.full_name
                    ? selectedConversationData.other_user.full_name
                        .slice(0, 2)
                        .toUpperCase()
                    : selectedConversationData?.other_user?.email
                    ? selectedConversationData.other_user.email
                        .slice(0, 2)
                        .toUpperCase()
                    : 'DM'}
                </div>
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {mode === 'support'
                    ? selectedTicket?.subject || 'Support Team'
                    : selectedConversationData?.other_user?.full_name ||
                      selectedConversationData?.other_user?.email ||
                      'Direct Message'}
                </h3>
                <p className="text-xs text-green-600">Online</p>
              </div>
            </div>
            {/* <Button
              variant="outline"
              size="icon"
              className="bg-transparent border-none shadow-none hover:bg-gray-100 hover:border-none hover:shadow-none hover:text-gray-900"
            >
              <Menu className="w-5 h-5" />
            </Button> */}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isOwn ? 'justify-end' : 'justify-start'
                  }`}
              >
                <div
                  className={`max-w-[70%] rounded-lg p-3 ${message.isOwn
                    ? 'bg-[#062136]'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                    }`}
                  style={message.isOwn ? { color: '#ffffff' } : undefined}
                >
                  <p className="text-sm">{message.content}</p>
                  <p
                    className={`text-xs mt-1 ${message.isOwn
                      ? 'text-blue-100'
                      : 'text-gray-500 dark:text-gray-400'
                      }`}
                  >
                    {message.time}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form
            onSubmit={handleSendMessage}
            className="p-4 border-t border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center space-x-2">
              <Button type="button" variant="ghost" size="icon">
                <Paperclip className="w-5 h-5" />
              </Button>
              <Input
                type="text"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="flex-1 p-5 dark:bg-slate-900 dark:border-slate-700"
                style={{ backgroundColor: '#f8f9fa', color: 'var(--input-text-color, #000)' }}
              />
              <Button
                type="submit"
                size="icon"
                className="bg-[#062136] hover:bg-[#062136]/90 text-white p-5"
                disabled={isSending}
              >
                <Send className="w-5 h-5 text-white" />
              </Button>
            </div>
          </form>
        </Card>
      </div>

      <Dialog open={isNewChatOpen} onOpenChange={setIsNewChatOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start a new chat</DialogTitle>
            <DialogDescription>
              Search users by email or name to begin a direct message.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by email or name"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleUserSearch}
                disabled={isSearchingUsers}
              >
                {isSearchingUsers ? 'Searching...' : 'Search'}
              </Button>
            </div>
            <div className="space-y-2">
              {userResults.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {userSearch.trim() ? 'No users found' : 'Search for a user to begin'}
                </p>
              ) : (
                userResults.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {user.full_name || 'User'}
                      </p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => handleStartConversation(user.id)}
                    >
                      Chat
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewChatOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesNew;
